<template>
  <v-card>
    <v-card-title class="justify-center">
      {{ title }}
    </v-card-title>
    <v-card-text class="px-2 px-sm-5">
      <LineChart
        :height="300"
        :chart-data="chartData"
        :chart-options="chartOptions"
      />
      <div
        v-if="loading"
        class="mt-1"
      >
        <v-progress-linear
          indeterminate
        />
      </div>
    </v-card-text>
  </v-card>
</template>

<script>

import 'chart.js/auto'
import { Chart } from 'chart.js'
import { Line } from 'vue-chartjs/legacy'
import dayjs from 'dayjs'
import ChartjsPluginWatermark from 'chartjs-plugin-watermark'
import ChartjsPluginDatalabels from 'chartjs-plugin-datalabels'

Chart.register(ChartjsPluginWatermark)
Chart.register(ChartjsPluginDatalabels)

export default {

  components: {
    LineChart: Line,
  },

  props: {
    title: {
      required: true,
      type: String,
    },
    data: {
      required: true,
      type: Array,
    },
    months: {
      required: true,
      type: Number,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    showLegend: {
      type: Boolean,
      default: true,
    },
    showDataLabel: {
      type: Boolean,
      default: true,
    },
  },

  computed: {
    visible() {
      if (!this.data.length) return []
      const last = this.data[this.data.length - 1]
      const from = dayjs(last.time).subtract(this.months, 'month').format('YYYY-MM-DD')

      return this.data.filter(p => p.time >= from)
    },

    mean() {
      if (!this.visible.length) return 0

      return this.visible.reduce((sum, p) => sum + p.value, 0) / this.visible.length
    },

    chartData() {
      const color = this.$vuetify.theme.currentTheme.primary

      return {
        labels: this.visible.map(p => p.time),
        datasets: [
          {
            label: this.title,
            borderColor: color,
            backgroundColor: color,
            borderWidth: 2.5,
            pointRadius: 0,
            tension: 0,
            fill: false,
            data: this.visible.map(p => p.value),
          },
          {
            label: `${this.$t('valuation_average')} (${this.mean.toFixed(1)})`,
            borderColor: '#9e9e9e',
            backgroundColor: '#9e9e9e',
            borderWidth: 1.5,
            borderDash: [6, 4],
            pointRadius: 0,
            tension: 0,
            fill: false,
            data: this.visible.map(() => this.mean),
          },
        ],
      }
    },

    chartOptions() {
      const lastIndex = this.visible.length - 1
      const { showDataLabel } = this

      return {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        layout: {
          padding: {
            top: 20,
            right: 20,
          },
        },
        scales: {
          x: {
            ticks: {
              autoSkip: true,
              maxTicksLimit: 10,
              maxRotation: 0,
              callback(value) {
                return dayjs(this.getLabelForValue(value)).format('MM/YY')
              },
            },
          },
          y: {
            position: 'left',
            beginAtZero: false,
            ticks: {
              callback(value) {
                return Number(value).toFixed(1)
              },
            },
          },
        },
        watermark: {
          image: require('@/assets/images/logos/logo.png'),

          width: 170,
          height: 47,
          opacity: 0.2,

          alignX: 'middle',
          alignY: 'middle',

          position: 'front',
        },
        plugins: {
          legend: {
            display: this.showLegend,
            position: 'bottom',
            labels: {
              boxWidth: 25,
              generateLabels(chart) {
                // The dashed average line would otherwise dash the legend box border too
                return Chart.defaults.plugins.legend.labels.generateLabels(chart)
                  .map(item => ({ ...item, lineDash: [] }))
              },
            },
          },
          tooltip: {
            callbacks: {
              title(items) {
                return dayjs(items[0].label).format('DD/MM/YYYY')
              },
              label(ctx) {
                return `${ctx.dataset.label}: ${Number(ctx.parsed.y).toFixed(2)}`
              },
            },
          },
          datalabels: {
            display(ctx) {
              return showDataLabel && ctx.datasetIndex === 0 && ctx.dataIndex === lastIndex
            },
            anchor: 'end',
            align: 'top',
            clamp: true,
            color: 'white',
            backgroundColor(ctx) {
              return ctx.dataset.borderColor
            },
            borderRadius: 3,
            padding: {
              top: 2,
              bottom: 0,
              right: 3,
              left: 3,
            },
            formatter(value) {
              return Number(value).toFixed(1)
            },
          },
        },
      }
    },
  },
}
</script>
